export interface RecordingConfig {
	videoBitrate?: number;
	audioBitrate?: number;
	frameRate?: number;
	audioEnabled?: boolean;
	videoQuality?: 'low' | 'medium' | 'high';
	maxDuration?: number;
	mimeType?: string;
	autoCapture?: boolean;
	autoDownload?: boolean;
	onRecordingStarted?: () => void;
	onRecordingStopped?: (data: { blob: Blob; url: string; filename: string }) => void;
	onRecordingPaused?: () => void;
	onRecordingResumed?: () => void;
	onTimeUpdated?: (data: { currentTime: number; maxDuration: number }) => void;
	onMaxDurationReached?: () => void;
	onError?: (data: { message: string }) => void;
	onDataAvailable?: (data: { blob: Blob; url: string; filename: string }) => void;
}

export interface RecordingState {
	isRecording: boolean;
	isPaused: boolean;
	currentTime: number;
	maxDuration: number;
	recordedBlob?: Blob;
	recordedUrl?: string;
	error?: string;
}

export type RecordingEventType = 'recording-started' | 'recording-stopped' | 'recording-paused' | 'recording-resumed' | 'time-updated' | 'max-duration-reached' | 'error' | 'data-available';

export interface RecordingEvent {
	type: RecordingEventType;
	data?: any;
}

type EventCallback = (event: RecordingEvent) => void;

export class MediaRecorderManager {
	private mediaRecorder: MediaRecorder | null = null;
	private displayStream: MediaStream | null = null;
	private audioStream: MediaStream | null = null;
	private combinedStream: MediaStream | null = null;
	private audioContext: AudioContext | null = null;

	private chunks: Blob[] = [];
	private startTime: number = 0;
	private pausedTime: number = 0;
	private totalPausedDuration: number = 0;
	private timer: ReturnType<typeof setInterval> | null = null;
	private maxDurationTimer: ReturnType<typeof setTimeout> | null = null;

	private eventListeners: Map<RecordingEventType, EventCallback[]> = new Map();

	private config: Required<RecordingConfig> = {
		videoBitrate: 2500000,
		audioBitrate: 128000,
		frameRate: 30,
		audioEnabled: true,
		videoQuality: 'high',
		maxDuration: 1200,
		mimeType: 'video/webm;codecs=vp8,opus',
		autoCapture: false,
		autoDownload: false,
		onRecordingStarted: undefined,
		onRecordingStopped: undefined,
		onRecordingPaused: undefined,
		onRecordingResumed: undefined,
		onTimeUpdated: undefined,
		onMaxDurationReached: undefined,
		onError: undefined,
		onDataAvailable: undefined
	};

	private state: RecordingState = {
		isRecording: false,
		isPaused: false,
		currentTime: 0,
		maxDuration: 1200,
		error: undefined
	};

	constructor(config?: Partial<RecordingConfig>) {
		if (config) {
			this.updateConfig(config);
		}

		try {
			this.getOptimalMimeType();
		} catch (error) {
			throw new Error('Bu tarayıcı kayıt formatını desteklemiyor');
		}

		this.setupQualitySettings();
		this.setupInternalEventListeners();
	}

	private setupInternalEventListeners(): void {
		this.on('recording-started', () => {
			this.config.onRecordingStarted?.();
		});

		this.on('recording-stopped', event => {
			this.config.onRecordingStopped?.(event.data);
		});

		this.on('recording-paused', () => {
			this.config.onRecordingPaused?.();
		});

		this.on('recording-resumed', () => {
			this.config.onRecordingResumed?.();
		});

		this.on('time-updated', event => {
			this.config.onTimeUpdated?.(event.data);
		});

		this.on('max-duration-reached', () => {
			this.config.onMaxDurationReached?.();
		});

		this.on('error', event => {
			this.config.onError?.(event.data);
		});

		this.on('data-available', event => {
			this.config.onDataAvailable?.(event.data);
		});
	}

	private on(eventType: RecordingEventType, callback: EventCallback): void {
		if (!this.eventListeners.has(eventType)) {
			this.eventListeners.set(eventType, []);
		}
		this.eventListeners.get(eventType)!.push(callback);
	}

	private emit(eventType: RecordingEventType, data?: any): void {
		const listeners = this.eventListeners.get(eventType);
		if (listeners) {
			listeners.forEach(callback => {
				callback({ type: eventType, data });
			});
		}
	}

	public updateConfig(newConfig: Partial<RecordingConfig>): void {
		this.config = { ...this.config, ...newConfig };
		this.state.maxDuration = this.config.maxDuration;
		this.setupQualitySettings();
	}

	private setupQualitySettings(): void {
		const qualitySettings = {
			low: { videoBitrate: 500000, frameRate: 24 },
			medium: { videoBitrate: 1000000, frameRate: 30 },
			high: { videoBitrate: 2000000, frameRate: 30 }
		};

		const settings = qualitySettings[this.config.videoQuality];
		this.config.videoBitrate = settings.videoBitrate;
		this.config.frameRate = settings.frameRate;
	}

	private async tryProgrammaticCapture(): Promise<MediaStream | null> {
		if ('getViewportMedia' in navigator.mediaDevices) {
			return await (navigator.mediaDevices as any).getViewportMedia({
				video: true,
				audio: this.config.audioEnabled
			});
		}

		if ('captureStream' in document.documentElement) {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d')!;
			canvas.width = window.innerWidth;
			canvas.height = window.innerHeight;

			const drawFrame = () => {
				ctx.fillStyle = '#ffffff';
				ctx.fillRect(0, 0, canvas.width, canvas.height);
			};

			drawFrame();
			return canvas.captureStream(this.config.frameRate);
		}

		return null;
	}

	public async requestPermissions(): Promise<boolean> {
		try {
			this.clearError();

			const programmaticStream = await this.tryProgrammaticCapture();
			if (programmaticStream) {
				this.displayStream = programmaticStream;
			} else {
				this.displayStream = await navigator.mediaDevices.getDisplayMedia({
					video: {
						mediaSource: 'browser',
						frameRate: this.config.frameRate,
						width: { ideal: 1920 },
						height: { ideal: 1080 }
					},
					audio: {
						echoCancellation: true,
						noiseSuppression: true,
						sampleRate: 44100
					},
					selfBrowserSurface: 'include',
					preferCurrentTab: true,
					surfaceSwitching: 'exclude',
					systemAudio: 'exclude'
				} as any);
			}

			if (this.config.audioEnabled) {
				try {
					this.audioStream = await navigator.mediaDevices.getUserMedia({
						audio: {
							echoCancellation: true,
							noiseSuppression: true,
							autoGainControl: true,
							sampleRate: 44100
						}
					});
				} catch (audioError) {
					console.warn('Mikrofon erişimi reddedildi:', audioError);
				}
			}

			return true;
		} catch (error) {
			console.log('error', error);
			return false;
		}
	}

	private getFileExtension(): string {
		const mimeType = this.config.mimeType.toLowerCase();
		if (mimeType.includes('mp4')) {
			return 'mp4';
		} else if (mimeType.includes('webm')) {
			return 'webm';
		}
		return 'webm';
	}

	public async startRecording(): Promise<boolean> {
		try {
			this.clearError();
			this.chunks = [];

			const hasPermissions = await this.requestPermissions();
			if (!hasPermissions) {
				throw new Error('Lütfen ekran ve mikrofon izinlerini verin');
			}

			await this.setupCombinedStream();
			await this.setupMediaRecorder();

			this.mediaRecorder!.start();

			this.state.isRecording = true;
			this.state.isPaused = false;
			this.state.currentTime = 0;
			this.chunks = [];
			this.startTime = Date.now();
			this.totalPausedDuration = 0;

			this.startTimer();
			this.setupMaxDurationTimer();
			this.setupStreamEndedHandler();

			this.emit('recording-started');
			return true;
		} catch (error) {
			this.handleError((error as Error).message);
			await this.cleanup();
			return false;
		}
	}

	public async stopRecording(): Promise<{
		blob: Blob;
		url: string;
		filename: string;
	} | null> {
		try {
			if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
				this.mediaRecorder.stop();
			}

			this.stopTimer();
			this.clearMaxDurationTimer();
			this.state.isRecording = false;
			this.state.isPaused = false;

			return new Promise(resolve => {
				const originalOnStop = this.mediaRecorder!.onstop;
				this.mediaRecorder!.onstop = async event => {
					if (originalOnStop) {
						originalOnStop.call(this.mediaRecorder, event);
					}

					const finalBlob = new Blob(this.chunks, {
						type: this.config.mimeType
					});

					const extension = this.getFileExtension();
					const filename = `doping-technology-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.${extension}`;
					const url = URL.createObjectURL(finalBlob);

					this.state.recordedBlob = finalBlob;
					this.state.recordedUrl = url;

					const result = { blob: finalBlob, url, filename };

					this.emit('recording-stopped', result);
					this.emit('data-available', result);

					await this.cleanup();

					if (this.config.autoDownload) {
						this.downloadRecording(filename);
					}

					resolve(result);
				};
			});
		} catch (error) {
			this.handleError((error as Error).message);
			await this.cleanup();
			return null;
		}
	}

	public pauseRecording(): boolean {
		try {
			if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
				this.mediaRecorder.pause();
			}

			this.state.isPaused = true;
			this.pausedTime = Date.now();
			this.stopTimer();

			this.emit('recording-paused');
			return true;
		} catch (error) {
			this.handleError((error as Error).message);
			return false;
		}
	}

	public resumeRecording(): boolean {
		try {
			if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
				this.mediaRecorder.resume();
			}

			this.state.isPaused = false;
			this.totalPausedDuration += Date.now() - this.pausedTime;
			this.startTimer();

			this.emit('recording-resumed');
			return true;
		} catch (error) {
			this.handleError((error as Error).message);
			return false;
		}
	}

	private async setupCombinedStream(): Promise<void> {
		if (!this.displayStream) {
			throw new Error('Display stream bulunamadı');
		}

		let combinedStream = this.displayStream;

		if (this.config.audioEnabled && (this.audioStream || this.displayStream.getAudioTracks().length > 0)) {
			this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
			const destination = this.audioContext.createMediaStreamDestination();

			if (this.displayStream.getAudioTracks().length > 0) {
				const systemAudio = this.audioContext.createMediaStreamSource(this.displayStream);
				systemAudio.connect(destination);
			}

			if (this.audioStream) {
				const micAudio = this.audioContext.createMediaStreamSource(this.audioStream);
				micAudio.connect(destination);
			}

			const videoTrack = this.displayStream.getVideoTracks()[0];
			const combinedAudioTrack = destination.stream.getAudioTracks()[0];

			if (combinedAudioTrack) {
				combinedStream = new MediaStream([videoTrack, combinedAudioTrack]);
			}
		}

		this.combinedStream = combinedStream;
	}

	private async setupMediaRecorder(): Promise<void> {
		if (!this.combinedStream) {
			throw new Error('Combined stream bulunamadı');
		}

		const mimeType = this.getOptimalMimeType();

		const options: MediaRecorderOptions = {
			mimeType,
			videoBitsPerSecond: this.config.videoBitrate,
			audioBitsPerSecond: this.config.audioBitrate
		};

		this.mediaRecorder = new MediaRecorder(this.combinedStream, options);

		this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
			if (event.data.size > 0) {
				this.chunks.push(event.data);
			}
		};

		this.mediaRecorder.onerror = (event: Event) => {
			this.handleError('MediaRecorder hatası: ' + ((event as any).error?.message || 'Bilinmeyen hata'));
		};
	}

	private getOptimalMimeType(): string {
		if (this.config.mimeType && MediaRecorder.isTypeSupported(this.config.mimeType)) {
			return this.config.mimeType;
		}

		const supportedTypes = ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];

		for (const type of supportedTypes) {
			if (MediaRecorder.isTypeSupported(type)) {
				this.config.mimeType = type;
				return type;
			}
		}

		throw new Error('Kayıt formatı bu tarayıcıda desteklenmiyor');
	}

	private startTimer(): void {
		this.timer = setInterval(() => {
			if (this.state.isRecording && !this.state.isPaused) {
				const elapsed = Math.floor((Date.now() - this.startTime - this.totalPausedDuration) / 1000);
				this.state.currentTime = elapsed;
				this.emit('time-updated', {
					currentTime: elapsed,
					maxDuration: this.state.maxDuration
				});
			}
		}, 1000);
	}

	private stopTimer(): void {
		if (this.timer) {
			clearInterval(this.timer);
			this.timer = null;
		}
	}

	private setupMaxDurationTimer(): void {
		if (this.config.maxDuration > 0) {
			this.maxDurationTimer = setTimeout(() => {
				this.emit('max-duration-reached');
				this.stopRecording();
			}, this.config.maxDuration * 1000);
		}
	}

	private clearMaxDurationTimer(): void {
		if (this.maxDurationTimer) {
			clearTimeout(this.maxDurationTimer);
			this.maxDurationTimer = null;
		}
	}

	private setupStreamEndedHandler(): void {
		if (this.displayStream) {
			const videoTrack = this.displayStream.getVideoTracks()[0];
			if (videoTrack) {
				videoTrack.addEventListener('ended', () => {
					if (this.state.isRecording) {
						this.stopRecording();
					}
				});
			}
		}
	}

	private async cleanup(): Promise<void> {
		if (this.state.recordedUrl) {
			URL.revokeObjectURL(this.state.recordedUrl);
			this.state.recordedUrl = undefined;
			this.state.recordedBlob = undefined;
		}

		this.stopTimer();
		this.clearMaxDurationTimer();

		this.displayStream?.getTracks().forEach(track => track.stop());
		this.audioStream?.getTracks().forEach(track => track.stop());
		this.combinedStream?.getTracks().forEach(track => track.stop());

		if (this.audioContext && this.audioContext.state !== 'closed') {
			await this.audioContext.close();
		}

		this.mediaRecorder = null;
		this.displayStream = null;
		this.audioStream = null;
		this.combinedStream = null;
		this.audioContext = null;
	}

	private handleError(message: string): void {
		this.state.error = message;
		this.emit('error', { message });
		console.error('MediaRecorderManager Error:', message);
	}

	private clearError(): void {
		this.state.error = undefined;
	}

	public getRecordingUrl(): string | null {
		return this.state.recordedUrl || null;
	}

	public getRecordingBlob(): Blob | null {
		return this.state.recordedBlob || null;
	}

	public getRecordingData(): {
		blob: Blob;
		url: string;
		filename: string;
	} | null {
		if (!this.state.recordedBlob || !this.state.recordedUrl) {
			return null;
		}

		const extension = this.getFileExtension();
		return {
			blob: this.state.recordedBlob,
			url: this.state.recordedUrl,
			filename: `doping-technology-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.${extension}`
		};
	}

	public getState(): Readonly<RecordingState> {
		return { ...this.state };
	}

	public getConfig(): Readonly<RecordingConfig> {
		return { ...this.config };
	}

	public formatTime(seconds: number): string {
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const secs = seconds % 60;

		if (hours > 0) {
			return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
		}
		return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
	}

	public downloadRecording(filename?: string): void {
		const extension = this.getFileExtension();
		const defaultFilename = `doping-technology-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.${extension}`;
		const a = document.createElement('a');
		a.href = this.state.recordedUrl!;
		a.download = filename || defaultFilename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}

	public destroy(): void {
		if (this.state.isRecording) {
			this.stopRecording();
		}
		this.cleanup();
		this.eventListeners.clear();

		if (this.state.recordedUrl) {
			URL.revokeObjectURL(this.state.recordedUrl);
		}
	}
}
